import React, { useEffect, useState, useMemo } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions,
  Button, TextField, Box, Alert, Typography, CircularProgress,
  Table, TableHead, TableBody, TableRow, TableCell, Chip, MenuItem,
} from '@mui/material';
import api from '../services/api.js';
import { useCkInside } from '@NP-Webber/crm-kit/dist/next/index.js';
import GradeFormDialogNext from './GradeFormDialogNext.jsx';

/**
 * דיאלוג להזנת/עדכון ציונים — מבוסס הרשמות.
 *
 * מצבי הפעלה:
 *  • `member` — טוען את כל ההרשמות של החבר (status=registered) ומציג שורה לכל
 *    מבחן (predefined) או לכל מסכת בתוך gemaraSelection (open-gemara).
 *    משתמש מזין ציון ליד כל שורה — ציונים שמולאו נשמרים בבת־אחת.
 *  • `exam` — טוען את כל ההרשמות למבחן ומציג שורה לכל חבר רשום.
 *  • `grade` (עריכה) — מסך פשוט של עדכון ציון בודד.
 *
 * אם לא נמצאו הרשמות, נופל למצב הזנה ידנית (קוד אישי / קוד מבחן + ציון).
 */
const IS_OPEN_GEMARA = process.env.REACT_APP_EXAM_MODE === 'open-gemara';

/** In the new design the same props render GradeFormDialogNext. */
const GradeFormDialog = (props) => {
  const inside = useCkInside();
  return inside ? <GradeFormDialogNext {...props} /> : <GradeFormDialogLegacy {...props} />;
};

const GradeFormDialogLegacy = ({ open, onClose, exam = null, member = null, grade = null, onSaved }) => {
  const isEdit = !!grade?._id;

  const [rows, setRows]               = useState([]);
  const [loading, setLoading]         = useState(false);
  const [saving, setSaving]           = useState(false);
  const [error, setError]             = useState('');
  const [results, setResults]         = useState(null);

  const [editScore, setEditScore]     = useState('');

  const [manualMode, setManualMode]   = useState(false);
  const [personalCode, setPersonalCode] = useState('');
  const [examCode, setExamCode]       = useState('');
  const [manualScore, setManualScore] = useState('');
  const [manualTractate, setManualTractate] = useState('');
  const [manualPeriodId, setManualPeriodId] = useState('');
  const [periods, setPeriods] = useState([]);
  const [resolvedMember, setResolvedMember] = useState(null);
  const [resolvedExam, setResolvedExam]     = useState(null);
  const [lookupLoading, setLookupLoading]   = useState(false);

  /** בונה רשימת שורות לפי הרשמות + ציונים קיימים */
  const buildRowsFromRegistrations = (registrations, grades, lockedSide) => {
    const newRows = [];
    const gradeMap = new Map();
    for (const g of grades) {
      const mId = g.memberId?._id || g.memberId;
      const eId = g.examId?._id || g.examId;
      const pId = g.examPeriodId?._id || g.examPeriodId;
      gradeMap.set(`${mId}|${eId || pId || 'gemara'}|${pId || ''}|${g.tractate || ''}`, g);
    }

    for (const reg of registrations) {
      if (reg.status !== 'registered') continue;
      const mId   = reg.memberId?._id || reg.memberId;
      const mName = reg.memberId
        ? `${reg.memberId.firstName || ''} ${reg.memberId.lastName || ''}`.trim()
        : '';
      const eId   = reg.examId?._id || reg.examId;
      const pId   = reg.periodId?._id || reg.periodId;
      const eName = reg.examId?.name || '';
      const eCode = reg.examId?.code || '';

      if (reg.gemaraSelection && reg.gemaraSelection.length > 0) {
        for (const sel of reg.gemaraSelection) {
          const key = `${mId}|${eId || pId || 'gemara'}|${pId || ''}|${sel.tractate}`;
          const existing = gradeMap.get(key);
          newRows.push({
            key,
            memberId: mId,
            memberName: mName,
            examId: eId || null,
            examPeriodId: pId || null,
            examName: eName || 'גמרא',
            examCode: eCode,
            tractate: sel.tractate,
            ranges: sel.ranges,
            existing,
            score: existing?.score ?? '',
          });
        }
      } else if (eId) {
        const key = `${mId}|${eId}|${pId || ''}|`;
        const existing = gradeMap.get(key);
        newRows.push({
          key,
          memberId: mId,
          memberName: mName,
          examId: eId,
          examPeriodId: pId || null,
          examName: eName,
          examCode: eCode,
          tractate: null,
          existing,
          score: existing?.score ?? '',
        });
      }
    }

    if (lockedSide === 'member') {
      newRows.sort((a, b) => (a.examName || '').localeCompare(b.examName || '', 'he'));
    } else {
      newRows.sort((a, b) => (a.memberName || '').localeCompare(b.memberName || '', 'he'));
    }
    return newRows;
  };

  useEffect(() => {
    if (!open) return;
    setError(''); setResults(null); setSaving(false);
    setLookupLoading(false); setManualMode(false);
    setPersonalCode(''); setExamCode('');
    setManualScore(''); setManualTractate(''); setManualPeriodId('');
    setResolvedMember(null); setResolvedExam(null);

    api.get('/exam-periods')
      .then(({ data }) => {
        const list = data.data || [];
        setPeriods(list);
        const active = list.find((p) => p.isActive);
        if (active) setManualPeriodId(active._id);
      })
      .catch(() => {});

    if (isEdit) {
      setEditScore(grade.score ?? '');
      setRows([]);
      return;
    }

    if (!member?._id && !exam?._id) {
      setManualMode(true);
      setRows([]);
      return;
    }

    const lockedSide = member?._id ? 'member' : 'exam';
    const params = lockedSide === 'member'
      ? { memberId: member._id, all: 1, status: 'registered' }
      : { examId: exam._id, all: 1, status: 'registered' };
    const gradeParams = lockedSide === 'member'
      ? { memberId: member._id, all: 1 }
      : { examId: exam._id, all: 1 };

    setLoading(true);
    Promise.all([
      api.get('/registrations', { params }),
      api.get('/grades', { params: gradeParams }),
    ])
      .then(([regRes, gradesRes]) => {
        const regs = regRes.data?.data || [];
        const gs   = gradesRes.data?.data || [];
        const built = buildRowsFromRegistrations(regs, gs, lockedSide);
        setRows(built);
        if (built.length === 0) setManualMode(true);
      })
      .catch((e) => setError(e.response?.data?.error || 'שגיאה בטעינת הרשמות'))
      .finally(() => setLoading(false));
  }, [open, isEdit, grade, member, exam]);

  const updateRowScore = (key, val) => {
    setRows((prev) => prev.map((r) => r.key === key ? { ...r, score: val } : r));
  };

  const stats = useMemo(() => {
    let toCreate = 0, toUpdate = 0, untouched = 0;
    for (const r of rows) {
      const s = r.score === '' || r.score === null ? null : parseFloat(r.score);
      if (s === null) { untouched++; continue; }
      if (r.existing) {
        if (r.existing.score !== s) toUpdate++;
        else untouched++;
      } else {
        toCreate++;
      }
    }
    return { toCreate, toUpdate, untouched };
  }, [rows]);

  const handleSaveBulk = async () => {
    setError('');
    const errs = [];
    let created = 0, updated = 0;
    setSaving(true);
    for (const r of rows) {
      const raw = r.score;
      if (raw === '' || raw === null || raw === undefined) continue;
      const s = parseFloat(raw);
      if (isNaN(s) || s < 0) {
        errs.push(`${r.examName}${r.tractate ? ` / ${r.tractate}` : ''}${r.memberName ? ` — ${r.memberName}` : ''}: ציון לא תקין (חייב להיות 0 ומעלה)`);
        continue;
      }
      try {
        if (r.existing) {
          if (r.existing.score === s) continue;
          await api.put(`/grades/${r.existing._id}`, {
            score: s,
            tractate: r.tractate || null,
            version: r.existing.version,
          });
          updated++;
        } else {
          const body = r.examId
            ? { memberId: r.memberId, examId: r.examId, examPeriodId: r.examPeriodId || null, score: s, tractate: r.tractate || null }
            : { memberId: r.memberId, examPeriodId: r.examPeriodId, score: s, tractate: r.tractate || null };
          await api.post('/grades', body);
          created++;
        }
      } catch (e) {
        errs.push(`${r.examName}${r.tractate ? ` / ${r.tractate}` : ''}${r.memberName ? ` — ${r.memberName}` : ''}: ${e.response?.data?.error || 'שגיאה'}`);
      }
    }
    setSaving(false);
    setResults({ created, updated, errors: errs });
    onSaved?.();
    if (errs.length === 0) setTimeout(() => onClose?.(), 1200);
  };

  const handleSaveEdit = async () => {
    setError('');
    const s = parseFloat(editScore);
    if (isNaN(s) || s < 0) return setError('ציון לא תקין (חייב להיות מספר חיובי)');
    setSaving(true);
    try {
      await api.put(`/grades/${grade._id}`, {
        score: s,
        tractate: grade.tractate || null,
        version: grade.version,
      });
      onSaved?.();
      onClose?.();
    } catch (e) {
      setError(e.response?.data?.error || 'שגיאת שמירה');
    } finally { setSaving(false); }
  };

  const lookupMember = async () => {
    if (!personalCode) return;
    setLookupLoading(true); setError('');
    try {
      const { data } = await api.get('/members', { params: { personalCode, pageSize: 1 } });
      if (data?.data?.length) setResolvedMember(data.data[0]);
      else { setResolvedMember(null); setError(`לא נמצא חבר עם קוד אישי ${personalCode}`); }
    } catch (e) { setError(e.response?.data?.error || 'שגיאת חיפוש'); }
    finally { setLookupLoading(false); }
  };
  const lookupExam = async () => {
    if (!examCode) return;
    setLookupLoading(true); setError('');
    try {
      const { data } = await api.get('/exams', { params: { code: examCode, pageSize: 1 } });
      if (data?.data?.length) setResolvedExam(data.data[0]);
      else { setResolvedExam(null); setError(`לא נמצא מבחן עם קוד ${examCode}`); }
    } catch (e) { setError(e.response?.data?.error || 'שגיאת חיפוש'); }
    finally { setLookupLoading(false); }
  };

  // האם להציג שדה מסכת במצב ידני — רק כשמדובר במבחן גמרא
  const effectiveExam  = exam?._id ? exam : resolvedExam;
  const isGemaraExam   = IS_OPEN_GEMARA ||
    effectiveExam?.type === 'time' && (effectiveExam?.subject || '').includes('גמרא');

  const handleSaveManual = async () => {
    setError('');
    const memberObj = member?._id ? member : resolvedMember;
    const examObj   = exam?._id   ? exam   : resolvedExam;
    const s = parseFloat(manualScore);
    if (!memberObj?._id) return setError('יש לבחור חבר תקין');
    if (!IS_OPEN_GEMARA && !examObj?._id) return setError('יש לבחור מבחן תקין');
    if (!manualPeriodId) return setError('יש לבחור מועד');
    if (isNaN(s) || s < 0) return setError('ציון לא תקין (חייב להיות מספר חיובי)');

    setSaving(true);
    try {
      const body = IS_OPEN_GEMARA
        ? {
            memberId: memberObj._id,
            examPeriodId: manualPeriodId,
            score: s,
            tractate: manualTractate || null,
          }
        : {
            memberId: memberObj._id,
            examId: examObj._id,
            examPeriodId: manualPeriodId,
            score: s,
            tractate: null,
          };
      await api.post('/grades', body);
      onSaved?.();
      onClose?.();
    } catch (e) {
      setError(e.response?.data?.error || 'שגיאת שמירה');
    } finally { setSaving(false); }
  };

  // האם יש בכלל שורות עם מסכת (רלוונטי למבחני גמרא בלבד)
  const hasTractateRows = rows.some((r) => r.tractate);

  const lockedSide = member?._id ? 'member' : exam?._id ? 'exam' : null;
  const headerLabel = isEdit
    ? 'עריכת ציון'
    : member?._id
      ? `הזנת ציונים — ${member.firstName || ''} ${member.lastName || ''}`.trim()
      : exam?._id
        ? `הזנת ציונים — ${exam.name || ''}${exam.code ? ` (קוד ${exam.code})` : ''}`
        : 'הזנת ציון';

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>{headerLabel}</DialogTitle>
      <DialogContent>
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
        {results && (
          <Alert severity={results.errors.length ? 'warning' : 'success'} sx={{ mb: 2 }}>
            נוצרו {results.created}, עודכנו {results.updated}
            {results.errors.length > 0 && (
              <Box component="ul" sx={{ m: 0, mt: 1, pl: 3 }}>
                {results.errors.map((er, i) => <li key={i}>{er}</li>)}
              </Box>
            )}
          </Alert>
        )}

        {isEdit && (
          <Box>
            <Typography variant="body2" color="text.secondary" gutterBottom>
              {grade?.examId?.name || ''}{grade?.tractate ? ` — ${grade.tractate}` : ''}
            </Typography>
            <TextField
              label="ציון" size="small" fullWidth type="number"
              inputProps={{ min: 0, step: 1 }}
              value={editScore}
              onChange={(e) => setEditScore(e.target.value)}
              autoFocus
            />
          </Box>
        )}

        {!isEdit && loading && (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress />
          </Box>
        )}

        {!isEdit && !loading && !manualMode && rows.length > 0 && (
          <>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
              הזן ציון לכל מבחן/מסכת. שורות ריקות לא יישמרו. שורות עם ציון זהה לקיים יידלגו.
            </Typography>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>{lockedSide === 'exam' ? 'חבר' : 'מבחן'}</TableCell>
                  {hasTractateRows && <TableCell>מסכת / דפים</TableCell>}
                  <TableCell>ציון קיים</TableCell>
                  <TableCell sx={{ width: 140 }}>ציון חדש</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {rows.map((r) => (
                  <TableRow key={r.key} hover>
                    <TableCell>
                      {lockedSide === 'exam' ? r.memberName : r.examName}
                      {lockedSide === 'member' && r.examCode ? (
                        <Typography variant="caption" color="text.secondary" sx={{ ml: 1 }}>
                          ({r.examCode})
                        </Typography>
                      ) : null}
                    </TableCell>
                    {hasTractateRows && (
                      <TableCell>
                        {r.tractate ? (
                          <>
                            {r.tractate}
                            {r.ranges?.length > 0 && (
                              <Typography variant="caption" color="text.secondary" sx={{ ml: 1 }}>
                                ({r.ranges.map((rg) => `${rg.from}–${rg.to}`).join(', ')})
                              </Typography>
                            )}
                          </>
                        ) : '—'}
                      </TableCell>
                    )}
                    <TableCell>
                      {r.existing ? (
                        <Chip
                          size="small"
                          label={r.existing.score}
                          color={r.existing.score >= 90 ? 'success' : 'default'}
                        />
                      ) : '—'}
                    </TableCell>
                    <TableCell>
                      <TextField
                        size="small" type="number"
                        inputProps={{ min: 0, step: 1 }}
                        value={r.score}
                        onChange={(e) => updateRowScore(r.key, e.target.value)}
                        sx={{ width: 110 }}
                      />
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
            <Box sx={{ mt: 1.5, display: 'flex', gap: 2, alignItems: 'center' }}>
              <Typography variant="caption" color="text.secondary">
                לשמירה: {stats.toCreate} חדשים, {stats.toUpdate} לעדכון, {stats.untouched} ללא שינוי
              </Typography>
              <Button size="small" onClick={() => setManualMode(true)}>הזנה ידנית</Button>
            </Box>
          </>
        )}

        {!isEdit && !loading && manualMode && (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            {rows.length === 0 && lockedSide && (
              <Alert severity="info">
                לא נמצאו הרשמות פעילות{lockedSide === 'member' ? ' לחבר זה' : ' למבחן זה'}.
                ניתן להוסיף ציון ידנית.
              </Alert>
            )}
            <Box>
              <Typography variant="subtitle2" gutterBottom>חבר</Typography>
              {member?._id ? (
                <Typography variant="body2">
                  {`${member.firstName || ''} ${member.lastName || ''}`.trim()}
                  {member.personalCode ? ` (קוד אישי: ${member.personalCode})` : ''}
                </Typography>
              ) : (
                <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                  <TextField
                    label="קוד אישי" size="small" type="number"
                    value={personalCode}
                    onChange={(e) => setPersonalCode(e.target.value.replace(/\D/g, ''))}
                    onBlur={lookupMember}
                    onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); lookupMember(); } }}
                  />
                  {lookupLoading && <CircularProgress size={20} />}
                  {resolvedMember && (
                    <Typography variant="body2" color="success.main">
                      ✓ {resolvedMember.firstName} {resolvedMember.lastName}
                    </Typography>
                  )}
                </Box>
              )}
            </Box>
            <Box>
              <Typography variant="subtitle2" gutterBottom>מבחן</Typography>
              {exam?._id ? (
                <Typography variant="body2">
                  {exam.name || ''}{exam.code ? ` (קוד: ${exam.code})` : ''}
                </Typography>
              ) : (
                <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                  <TextField
                    label="קוד מבחן" size="small"
                    value={examCode}
                    onChange={(e) => setExamCode(e.target.value)}
                    onBlur={lookupExam}
                    onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); lookupExam(); } }}
                  />
                  {lookupLoading && <CircularProgress size={20} />}
                  {resolvedExam && (
                    <Typography variant="body2" color="success.main">
                      ✓ {resolvedExam.name}
                    </Typography>
                  )}
                </Box>
              )}
            </Box>
            <TextField
              select label="מועד" size="small" fullWidth required
              value={manualPeriodId}
              onChange={(e) => setManualPeriodId(e.target.value)}
            >
              {periods.map((p) => (
                <MenuItem key={p._id} value={p._id}>{p.name}</MenuItem>
              ))}
            </TextField>
            {isGemaraExam && (
              <TextField
                label="מסכת (אופציונלי)"
                size="small" fullWidth
                value={manualTractate}
                onChange={(e) => setManualTractate(e.target.value)}
              />
            )}
            <TextField
              label="ציון"
              size="small" fullWidth type="number"
              inputProps={{ min: 0, step: 1 }}
              value={manualScore}
              onChange={(e) => setManualScore(e.target.value)}
            />
            {rows.length > 0 && (
              <Button size="small" onClick={() => setManualMode(false)}>
                חזרה לרשימת הרשמות
              </Button>
            )}
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={saving}>סגור</Button>
        {isEdit && (
          <Button variant="contained" onClick={handleSaveEdit} disabled={saving}>
            {saving ? 'שומר...' : 'שמור'}
          </Button>
        )}
        {!isEdit && manualMode && (
          <Button variant="contained" onClick={handleSaveManual} disabled={saving}>
            {saving ? 'שומר...' : 'שמור ציון'}
          </Button>
        )}
        {!isEdit && !manualMode && rows.length > 0 && (
          <Button
            variant="contained"
            onClick={handleSaveBulk}
            disabled={saving || (stats.toCreate + stats.toUpdate === 0)}
          >
            {saving ? 'שומר...' : `שמור ${stats.toCreate + stats.toUpdate} ציונים`}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default GradeFormDialog;
